/**
 * The field being typed into stays where the cashier can see it (2026-10-07).
 *
 * A dialog shrinks when a keyboard comes up — the phone's (it docks above it,
 * lib/keyboard-inset.ts) or the till's own (globals.css moves it up while the
 * pad holds the bottom). It shrinks over its 300 ms transition, so a check
 * made when the keyboard appears sees the dialog still tall and the field
 * still in sight; a third of a second later the field is below the edge.
 * The owner's phone and a narrow till window both showed 0 px of the close
 * dialog's count. So the field is checked again every time its dialog
 * changes size, and the dialog's scroller moves it back into sight.
 */
import type { Rect } from './touch-keyboard'

/** The dialogs a focused field is kept in sight in. */
const POPUP = '[data-slot="dialog-content"], [data-slot="alert-dialog-content"]'

/**
 * How far a scroller (`box` = the part of it on screen) must move up so the
 * field is seen: above the pad when the pad is under it, and inside the box.
 * The box counts on its own: a field left below a dialog body's edge — the
 * close dialog's count under its footer — is as hidden as one under a key.
 * 0 = in sight.
 */
export function revealShift(field: Rect, box: Rect, pad: Rect | null, gap = 12): number {
  const underPad = pad !== null && field.left < pad.right && pad.left < field.right
  const bottom = Math.min(box.bottom, underPad ? pad.top : Infinity)
  return Math.max(0, field.bottom + gap - bottom)
}

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
}

/** Scroll the nearest scrollable ancestor that can, until revealShift says 0. */
export function bringIntoSight(el: Element, pad: Rect | null) {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
      const b = p.getBoundingClientRect()
      const top = b.top + p.clientTop
      const need = revealShift(rectOf(el), { left: b.left, right: b.right, top, bottom: top + p.clientHeight }, pad)
      if (need <= 0) continue
      const before = p.scrollTop
      p.scrollTop = before + need
      if (p.scrollTop !== before) return
    }
  }
}

const NOT_TYPED = new Set(['checkbox', 'radio', 'file', 'range', 'color', 'button', 'submit', 'reset', 'image', 'hidden'])

/** An <input> of this type is typed into — a keyboard comes up for it. */
export function typedInto(inputType: string): boolean {
  return !NOT_TYPED.has(inputType.toLowerCase())
}

const isTypedField = (el: unknown): el is HTMLInputElement | HTMLTextAreaElement =>
  el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && typedInto(el.type))

/** Once, at start-up: watch the dialog of whatever field gets the focus. */
export function keepFocusedFieldInSight() {
  if (typeof ResizeObserver === 'undefined') return
  let watched: Element | null = null
  const ro = new ResizeObserver(() => {
    const el = document.activeElement
    if (watched && isTypedField(el) && watched.contains(el)) bringIntoSight(el, null)
  })
  document.addEventListener('focusin', (e) => {
    const el = e.target
    if (!isTypedField(el)) return
    const popup = el.closest(POPUP)
    if (popup === watched) return
    if (watched) ro.unobserve(watched)
    watched = popup
    if (popup) ro.observe(popup)
  }, true)
}
